import { RefObject } from 'react';
import styled, { css } from 'styled-components';
import capitalize from 'lodash/capitalize';
import VirtualizedRow from '#/components/rfq-common/TableRow';

import {
  lima,
  paradigmLightBlue,
  razzmatazz,
} from '@paradigm/design-system/src/colors';

import { hourFormat, shortDateFormat } from '@paradigm/utils/src/date';
import { GrfqQuote } from '@paradigm/features/src/unified-rfqs/entities/grfq/domain';
import { Order } from '@paradigm/features/src/unified-rfqs/entities/shared';
import { formatProductQty } from '@paradigm/features/src/unified-rfqs/ui/decimal-format';
import {
  getProductName,
  findBaseProductCode,
} from '@paradigm/features/src/products/domain';
import { Order as DrfqOrder } from '@paradigm/features/src/unified-rfqs/entities/drfq/orders';
import { useMaybeOrder } from '@paradigm/features/src/unified-rfqs/contexts/unified-orders';
import useFetchedDrfq from '@paradigm/features/src/unified-rfqs/contexts/use-fetched-drfq';
import { QuickActions } from '#/components/UnifiedRfqs/components/QuickActions';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { StrategyDescription } from '#/components/UnifiedRfqs/components/strategy-description/StrategyDescription';
import { useContextMenuActions } from '#/components/UnifiedRfqs/context-menu/context-menu';
import { LongText } from '#/components/UnifiedRfqs/components/LongText';
import BottomTableRow, {
  DesktopRow,
} from '#/components/UnifiedRfqs/BottomTableRow';
import makeColumns from '#/components/UnifiedRfqs/orders-history/makeColumns';
import { RfqProvider } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import {
  useFormattedDrfqMakerPrice,
  useFormattedGrfqMakerPrice,
} from '@paradigm/features/src/unified-rfqs/ui/hooks/useFormattedPrice';

interface Props {
  readonly orderId: Order['id'];
  readonly scrollableRef: RefObject<HTMLDivElement>;
}

export const CLOSED_ORDER_ROW_HEIGHT = 42;

export default function OrderHistoryRow(props: Props) {
  const { scrollableRef } = props;
  const { orderId } = props;

  return (
    <VirtualizedRow
      id={orderId}
      scrollableRef={scrollableRef}
      rowContent={() => <OrderRowContent orderId={orderId} />}
    />
  );
}

function OrderRowContent({ orderId }: { orderId: Order['id'] }) {
  const order = useMaybeOrder(orderId);
  if (order == null) {
    return null;
  }

  return (
    <RfqProvider rfqId={order.rfq_id}>
      <RfqOrderHistoryRow order={order} />
    </RfqProvider>
  );
}

function RfqOrderHistoryRow({ order }: { order: Order }) {
  switch (order.rfqType) {
    case 'grfq':
      return <GrfqOrderHistoryRow quote={order} />;
    case 'drfq':
      return <DrfqOrderHistoryRow order={order} />;
    default:
      throw new TypeError(
        'Unexpected order history row with invalid rfq type.',
      );
  }
}

function DrfqOrderHistoryRow({ order }: { order: DrfqOrder }) {
  const { drfq } = useFetchedDrfq(order.rfq_id);
  const contextMenu = useContextMenuActions();
  const productCode = findBaseProductCode(drfq?.product_codes ?? []);
  const qtyFormatter = (value: string | number) =>
    formatProductQty(value, productCode, true);
  const formattedPrice = useFormattedDrfqMakerPrice({
    price: order.price,
    side: order.side,
    drfqInfo: drfq,
  });
  const actions = (
    <QuickActions
      baseDDTag="Order History Quick Actions"
      prefillAccount={order.account_name}
      productCode={productCode}
      rfqId={order.rfq_id}
    />
  );
  const description = (
    <StrategyDescription>{drfq?.description ?? '-'}</StrategyDescription>
  );
  const side = <ActionLabel>{order.side}</ActionLabel>;
  const orderId = <LongText tooltip={order.id}>{order.id}</LongText>;
  const accountName = (
    <LongText tooltip={order.account_name}>{order.account_name}</LongText>
  );

  const columns = makeColumns({
    time: hourFormat(order.last_updated_at),
    auction: 'RFQ',
    product: getProductName(productCode),
    description,
    actions,
    status: capitalize(order.state),
    type: capitalize(order.type),
    qty: qtyFormatter(order.quantity),
    price: formattedPrice,
    side,
    completed: qtyFormatter(order.filled_quantity),
    canceled: qtyFormatter(order.canceled_quantity),
    id: orderId,
    account: accountName,
    date: shortDateFormat(order.last_updated_at),
  });

  return (
    <Row
      columns={columns}
      dataTestId={testId(order)}
      onContextMenu={({ x, y }) => {
        contextMenu.open({
          rfqId: order.rfq_id,
          account: order.account_name,
          detailsItemId: order.id,
          anchorPoint: { x, y },
        });
      }}
    />
  );
}

function GrfqOrderHistoryRow({ quote }: { quote: GrfqQuote }) {
  const contextMenu = useContextMenuActions();
  const productCode = findBaseProductCode(quote.product_codes ?? []);
  const qtyFormatter = (value: string | number) =>
    formatProductQty(value, productCode);
  const formattedPrice = useFormattedGrfqMakerPrice({
    price: quote.price,
    productCode,
    side: quote.side,
    description: quote.description,
    /** The quote entity for TAKERS has an order obj */
    isTaker: quote.order != null,
  });

  const actions = (
    //  WIP: PTP-18877: Include account from trade object when available
    <QuickActions
      baseDDTag="Order History Quick Actions"
      rfqId={quote.rfq_id}
      productCode={productCode}
    />
  );
  const side = <ActionLabel>{quote.side}</ActionLabel>;
  const description = (
    <StrategyDescription>{quote.description}</StrategyDescription>
  );

  const columns = makeColumns({
    time: hourFormat(quote.last_activity),
    auction: 'OB',
    product: getProductName(productCode),
    description,
    actions,
    status: capitalize(quote.status),
    type: capitalize(quote.type),
    qty: qtyFormatter(quote.original_quantity),
    price: formattedPrice,
    side,
    completed: qtyFormatter(quote.completed_quantity),
    canceled: qtyFormatter(quote.canceled_quantity),
    id: quote.id,
    account: quote.account,
    date: shortDateFormat(quote.last_activity),
  });

  return (
    <Row
      columns={columns}
      onContextMenu={({ x, y }) => {
        contextMenu.open({
          //  WIP: PTP-18877: Include account from trade object when available
          rfqId: quote.rfq_id,
          detailsItemId: quote.id,
          anchorPoint: { x, y },
          productCode,
        });
      }}
      dataTestId={testId(quote)}
    />
  );
}

export function OrderHistoryHeaderRow() {
  const columns = makeColumns().map((col) => (
    <span key={col.id}>{col.name}</span>
  ));
  return <HeaderRow>{columns}</HeaderRow>;
}

function testId(
  order: Pick<DrfqOrder, 'id'> | Pick<GrfqQuote, 'id'>,
): string | undefined {
  return `urfq-order-history-${order.id}`;
}

const GRID_CSS = css`
  grid-template-columns: 68px 50px 50px 120px 250px 115px 80px 80px 80px 100px 80px 80px 80px 220px 200px;
  grid-template-areas: 'date time auction prod desc actions status type qty price side completed canceled id account';
`;

const Row = styled(BottomTableRow)`
  ${GRID_CSS}

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    .mobile-bottom-table-row {
      display: grid;
      grid-template-columns: 30px 25px auto;
    }
  }
`;

const HeaderRow = styled(DesktopRow)`
  ${GRID_CSS}
  position: sticky;
  top: 0;
  z-index: 1;
  color: ${paradigmLightBlue};

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const ActionLabel = styled.span`
  font-size: 14px;
  ${(props) => props.children === 'SELL' && `color: ${razzmatazz};`}
  ${(props) => props.children === 'BUY' && `color: ${lima};`}
`;
